// MEETING MODE — v4.0.0 (Karan's design 23/10/2026: Track-meeting-mode.pdf p3–p8,
// the motion in Track-meeting-mode.mp4; docs/meeting-mode/SPEC.md).
//
// Start (who is in, the order, how to run it) → one person at a time (their
// projects with what moved since the last meeting, their tasks grouped, their
// week, a countdown ring) → No owner (give each unowned thing to someone) →
// Wrap up (actions, changes, notes; send the notes to the team).
//
// LAWS THAT BEAT THE DRAWING (SPEC.md, "Conflicts"):
//  · D2 — the stage picker is the shared forward-only one, and a move takes a
//    second press before anything is written.
//  · Fail conservative — "Send notes to the team" takes two presses; the email
//    goes to the meeting's own people only, never a client.
//  · D5 — the second office, the mailbox and the people come from the company's
//    own settings and users; nothing here names ASP.
//  · Money — no fees, hours or money anywhere on these screens.
//
// EVERY WRITE IS AN EXISTING ONE. Actions are tasks (createTask), stage and lead
// go through the Projects screen's own applyStatus / applyLead (passed in), a new
// date through the project edit panel's write, notes through addNote. The only
// new record is the meeting itself (meetings/{id} — awaiting sign-off), which
// holds ids, never the text of a task or a note.
//
// STAYING CURRENT (v4.6.3, Karan 26/10/2026 — "when I change the status etc
// during a meeting, it doesn't update within the meeting"). Three things went
// stale: the room snapshot was only written when the meeting moved to the next
// person, a follower only redrew on a new person and read its own copy of the
// projects, and the selected row was a POSITION, so a new date (which re-sorts
// the list) left the meeting on a different project. Now every change writes
// the room snapshot with it, a follower applies the runner's recorded changes
// and redraws, the selection follows the project, and a live listener
// (projects written since the meeting opened, and the open tasks) folds in
// changes made anywhere else — redrawn without losing the meeting's place,
// and held while a confirm, an editor or a typed action is open.
//
// Every value renders through escapeHtml (house rule 0).

import {
  createTask, assignTask, addNote, addRecordChange, updateProjectFields, listLeaveRequests,
  createMeeting, updateMeeting, watchMeeting, followMeeting, runningMeetings, recentMeetings,
  changesSince, tasksDoneSince, notesSince, projectEmails, roomConfig,
  watchProjectsUpdatedSince, watchTeamTasks, leadRefPath,
} from '../db.js';
import { escapeHtml, titleCase } from '../ui.js';
import { navIcon } from '../nav-icons.js';
import { hasFeature } from '../features.js';
import { orgSetting } from '../org.js';
import { offices } from '../header.js';
import { teamFrom, meIn, dayKey } from '../tasks.js';
import { isPerson } from '../people.js';
import { parseTaskLine } from '../task-parse.js';
import { nextActionFor } from './projects-next.js';
import { openStagePicker, stageText } from './project-menus.js';
import {
  NOOWNER, WRAP, remoteOffice, onLeave, ORDER_PRESETS, presetLabel, orderPeople, projectsLedBy, isLive,
  leadOf, isOpenTask, isLateTask, groupTasks, taskWhen, heldText, weekStrip, projectSince, movedLine,
  historyLines, movedCount, fromLastTime, unownedTasks, unledProjects, suggestOwner, actionChips,
  roomSnapshot, meetingDefaults, ringLeft, ringText, meetingLive, dm, dowDm, longDayDm, longDate, weekdayName, hhmm,
  firstName, initialsOf, stageLabel, mergeProjects, mergeTasks, applyRecordedChanges, pinSelection,
} from '../meeting-model.js';

const REVIEWER_ROLES = ['Owner', 'Director', 'Finance', 'Coordinator'];
const RUNNER_ROLES = ['Owner', 'Director', 'Office'];
const WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve'];
const word = (n) => (n >= 0 && n < WORDS.length ? WORDS[n] : String(n));
const esc = escapeHtml;
const kbd = (k) => `<span class="mm-kbd">${esc(k)}</span>`;
const av = (initials, cls = '') => `<span class="mm-av${cls ? ` ${cls}` : ''}" aria-hidden="true">${esc(initials || '?')}</span>`;
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

let openNow = null;   // one meeting overlay at a time

/** Is somebody running a meeting this person could follow (the Projects button)? */
export async function followableMeeting(session, users = {}) {
  const list = await runningMeetings().catch(() => []);
  const me = String((session && session.email) || '').toLowerCase();
  const live = list.filter((m) => meetingLive(m) && String(m.runnerEmail || '').toLowerCase() !== me);
  if (!live.length) return null;
  const meUser = Object.entries(users).find(([, u]) => String(u.email || '').toLowerCase() === me);
  const home = String((offices()[0] || {}).label || '').toLowerCase();
  const remote = meUser && meUser[1].office && String(meUser[1].office).toLowerCase() !== home;
  return live.find((m) => m.remoteFollow && remote) || null;
}

/** A meeting this person started (or may run) that is still going — "Resume meeting". */
export async function resumableMeeting(session) {
  const list = await runningMeetings().catch(() => []);
  const me = String((session && session.email) || '').toLowerCase();
  return list.find((m) => meetingLive(m) && String(m.runnerEmail || '').toLowerCase() === me) || null;
}

/**
 * @param o.projects   the live portfolio (Projects screen's read)
 * @param o.users      usersById
 * @param o.tasks      open tasks (teamTasks)
 * @param o.session    the signed-in person
 * @param o.applyStatus(p, next)  Projects' forward-only status write
 * @param o.applyLead(p, userId)  Projects' lead write
 * @param o.follow     a meeting record to follow read-only (remote office)
 * @param o.onClose    called after the overlay closes (Projects redraws)
 */
export function openMeeting(o) {
  if (openNow) return;
  const { session } = o;
  const projects = (o.projects || []).filter((p) => !p.isTest);
  const users = o.users || {};
  const tasks = (o.tasks || []).slice();
  const projectsById = Object.fromEntries(projects.map((p) => [p.id, p]));
  const roles = (session && session.roles) || [];
  const canEdit = roles.some((r) => REVIEWER_ROLES.includes(r));
  const today = dayKey();
  const offs = offices();

  const root = document.createElement('div');
  root.className = 'mm';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-label', 'Team meeting');
  root.innerHTML = `<div class="mm-top"><span class="mm-word">Track<span class="mm-dot">.</span></span>
      <span class="mm-title"><b>Team meeting</b></span><span class="mm-grow"></span>
      <button type="button" class="mm-btn mm-close" data-a="close">Close ${kbd('Esc')}</button></div>
    <div class="mm-load">Getting the team ready…</div>`;
  document.body.appendChild(root);
  document.body.classList.add('mm-open');
  root.querySelector('[data-a="close"]').addEventListener('click', () => close());
  openNow = root;

  // ── State ──────────────────────────────────────────────────────────────
  const S = {
    phase: 'start', meeting: null, mode: 'walkthrough', preset: 'remote-first', order: [], minutes: 3,
    roomFollows: false, remoteFollow: false, idx: 0, sel: 0, nsel: 0, personStartedAt: new Date().toISOString(),
    startedAt: null, actionIds: [], made: {}, changes: [], notes: [], given: {}, armed: null, editing: null,
    barProject: null, followers: {}, hint: true, noDate: false, saving: false, readOnly: false,
    selPid: null,   // the selected row's project — the selection follows it, not its position
    out: new Set(), // left out by default (timesheets not required, nothing live — meetingDefaults); Add brings one in for THIS meeting
    dirty: false,   // something changed while the meeting was busy: redraw when it is free
  };
  const openedAt = new Date().toISOString();
  const applied = new Set();   // a follower's record changes already applied (applyRecordedChanges)
  const unsubs = [];           // live listeners, stopped on close
  let people = [], me = null, remote = null, leave = [], last = null, live = [], room = null;
  let since = '', changes = [], done = [], notes = [];
  const emails = {};   // projectNo → count since the last meeting
  const timers = [];
  let stopWatch = null;

  const away = () => new Set(people.filter((p) => onLeave(p.email, leave, today)).map((p) => p.userId));
  // Who the order is built from: not on leave, not left out of this meeting.
  const inMeeting = () => { const off = away(); return people.filter((p) => !off.has(p.userId) && !S.out.has(p.userId)); };
  const personOf = (id) => people.find((p) => p.userId === id) || null;
  const seq = () => [...S.order, NOOWNER, WRAP];
  const cur = () => seq()[S.idx];
  const openTasksOf = (id) => tasks.filter((t) => isOpenTask(t) && t.assigneeId === id);
  // The selected row, by project: S.sel is kept as the index the list shows.
  const pinSel = (c) => {
    const list = c && c !== NOOWNER && c !== WRAP ? projectsLedBy(projects, c) : [];
    const r = pinSelection(list, S.selPid, S.sel);
    S.sel = r.sel; S.selPid = r.selPid;
    return list;
  };
  const selectRow = (i) => { const list = projectsLedBy(projects, cur()); S.sel = i; S.selPid = list[i] ? list[i].id : null; };
  const liveOf = (id) => projectsLedBy(projects, id).length;
  const userName = (id) => { const p = personOf(id); return p ? p.name : titleCase((users[id] && users[id].displayName) || id || ''); };
  const nameByEmail = (e) => { const p = people.find((x) => x.email === String(e || '').toLowerCase()); return p ? firstName(p.name) : firstName(String(e || '').split('@')[0]); };
  const clientOf = (p) => (p && (p.clientName || p.title)) || '';

  // ── Load ───────────────────────────────────────────────────────────────
  (async () => {
    try {
      const [lv, recent, running, rm] = await Promise.all([
        listLeaveRequests().catch(() => []), recentMeetings().catch(() => []),
        runningMeetings().catch(() => []), roomConfig('main').catch(() => null)]);
      leave = Array.isArray(lv) ? lv : [];
      last = (Array.isArray(recent) ? recent : [])[0] || null;
      live = (Array.isArray(running) ? running : []).filter((m) => meetingLive(m));
      room = rm && !Array.isArray(rm) && typeof rm === 'object' ? rm : null;
      const list = Object.entries(users).map(([id, u]) => ({ id, ...u }));
      // The team as Tasks reads it, plus the people Tasks leaves out because they
      // have no email (a contractor's site leads and operatives sign in by phone):
      // they lead projects, so they are in the meeting — they just cannot be
      // handed a task (the rules pair a task's assignee id with an email).
      const sys = (id) => { const r = users[id] && users[id].roles; return Array.isArray(r) && r.length ? r[0] : ''; };
      const noEmail = list.filter((u) => u.active !== false && !u.email && isPerson(u))
        .map((u) => ({ userId: u.id, name: u.displayName || u.id, email: '', initials: initialsOf(u.displayName || u.id),
          role: u.jobTitle || sys(u.id) || '', statusToday: 'in_office' }));
      people = teamFrom(list, leave, today).concat(noEmail)
        .map((p) => ({ ...p, office: (users[p.userId] && users[p.userId].office) || '', sysRole: sys(p.userId) || p.role }));
      me = meIn(people, session && session.email);
      remote = remoteOffice(offs, people);
      since = (last && last.startedAt) || new Date(Date.now() - 7 * 86400000).toISOString();
      [changes, done, notes] = await Promise.all([
        changesSince(since).catch(() => []), tasksDoneSince(since).catch(() => []), notesSince(since).catch(() => [])]);
      changes = Array.isArray(changes) ? changes : []; done = Array.isArray(done) ? done : []; notes = Array.isArray(notes) ? notes : [];
      S.roomFollows = !!room;
      S.remoteFollow = !!remote;
      S.out = new Set(meetingDefaults(people, { users, projects, tasks }).out.map((p) => p.userId));
      S.order = orderPeople(inMeeting(), S.preset,
        { live: liveOf, meId: me && me.userId, remote, lastOrder: last && last.order });
      if (o.follow) return startFollowing(o.follow);
      const mine = live.find((m) => String(m.runnerEmail || '').toLowerCase() === String(session.email || '').toLowerCase());
      if (mine) return resume(mine);
      drawStart();
    } catch (e) {
      root.querySelector('.mm-load').textContent = `The meeting could not be set up — ${e && e.message ? e.message : e}`;
    }
  })();

  // ── Keys ───────────────────────────────────────────────────────────────
  const onKey = (ev) => {
    if (!root.isConnected) return;
    if (document.querySelector('.pmenu, dialog[open], .tf-scrim')) return;
    if (ev.metaKey || ev.ctrlKey) return;
    const a = document.activeElement;
    const typing = a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.tagName === 'SELECT' || a.isContentEditable);
    const k = ev.key;
    if (k === 'Escape') {
      ev.preventDefault();
      if (typing && a.blur) { a.blur(); return; }
      if (S.armed || S.editing) { S.armed = null; S.editing = null; drawBody(); return; }
      close(); return;
    }
    if (typing) return;
    if (S.phase === 'start') {
      if (k === 'Enter' && !(a && a.closest && a.closest('button, select'))) { ev.preventDefault(); start(); }
      if (ev.altKey && (k === 'ArrowUp' || k === 'ArrowDown') && a && a.dataset && a.dataset.uid) {
        ev.preventDefault(); moveInOrder(a.dataset.uid, k === 'ArrowUp' ? -1 : 1);
      }
      return;
    }
    if (ev.altKey) return;
    const c = cur();
    if (k === 'ArrowRight') { ev.preventDefault(); go(S.idx + 1); return; }
    if (k === 'ArrowLeft') { ev.preventDefault(); go(S.idx - 1); return; }
    if (S.phase === 'follow') return;
    if (c === WRAP) {
      if (k === 'Enter' && !(a && a.closest && a.closest('button'))) { ev.preventDefault(); sendNotes(); }
      return;
    }
    if (k === 'ArrowDown' || k === 'ArrowUp') {
      ev.preventDefault();
      const d = k === 'ArrowDown' ? 1 : -1;
      if (c === NOOWNER) { const n = noOwnerItems().length; S.nsel = Math.max(0, Math.min(n - 1, S.nsel + d)); drawBody(); return; }
      const n = S.mode === 'walkthrough' ? pinSel(c).length : 0;
      if (n) { selectRow(Math.max(0, Math.min(n - 1, S.sel + d))); S.armed = null; S.editing = null; drawBody(); }
      return;
    }
    if (c === NOOWNER) return;
    const lk = k.toLowerCase();
    if (lk === 'a') { ev.preventDefault(); focusBar(null); return; }
    const p = selectedProject();
    if (!p || S.mode !== 'walkthrough') return;
    if (lk === 's') { ev.preventDefault(); root.querySelector('[data-row-act="stage"]')?.click(); }
    if (lk === 'd') { ev.preventDefault(); root.querySelector('[data-row-act="date"]')?.click(); }
    if (lk === 'n') { ev.preventDefault(); root.querySelector('[data-row-act="note"]')?.click(); }
  };
  document.addEventListener('keydown', onKey, true);

  function close() {
    document.removeEventListener('keydown', onKey, true);
    timers.forEach((t) => clearInterval(t));
    if (stopWatch) { try { stopWatch(); } catch (_) { /* gone */ } }
    unsubs.splice(0).forEach((u) => { try { u(); } catch (_) { /* gone */ } });
    clearTimeout(roomTimer);
    if (S.phase === 'follow' && S.meeting && session && session.uid) followMeeting(S.meeting.id, session.uid, null).catch(() => {});
    root.classList.add('mm-leaving');
    setTimeout(() => { root.remove(); document.body.classList.remove('mm-open'); }, 160);
    openNow = null;
    if (typeof o.onClose === 'function') o.onClose();
  }
  const toast = (msg) => {
    const t = document.createElement('div');
    t.className = 'mm-toast'; t.setAttribute('role', 'status'); t.textContent = msg;
    root.appendChild(t);
    setTimeout(() => t.remove(), 3200);
  };

  // ══ 1. START ═══════════════════════════════════════════════════════════
  function drawStart() {
    S.phase = 'start';
    const now = new Date();
    const off = away();
    const inOrder = S.order.map(personOf).filter(Boolean);
    const skipped = people.filter((p) => off.has(p.userId) && !S.out.has(p.userId));
    const notIn = people.filter((p) => S.out.has(p.userId)).sort((a, b) => String(a.name).localeCompare(String(b.name)));
    const week = (p) => weekStrip(p, { leave, tasks: tasks.concat(done), projectsById, today });
    const unowned = unownedTasks(tasks).length; const unled = unledProjects(projects).length;
    const lastDay = last ? weekdayName(last.startedAt) : '';
    const head = weekStrip(null, { today }).map((d) => `<span class="${d.today ? 'is-today' : ''}">${esc(d.dow.toUpperCase())} ${esc(String(d.date))}</span>`).join('');
    const row = (p, skip) => {
      const open = openTasksOf(p.userId);
      const late = open.filter((t) => isLateTask(t, today)).length;
      const mv = last ? movedCount(p.userId, { projects, changes, tasksDone: done, since }) : null;
      const liveN = liveOf(p.userId);
      const days = week(p).map((d) => `<span class="mm-sday ${d.kind}">${d.kind === 'office' ? '<i></i>' : `<em>${esc(d.kind === 'site' ? 'Site' : 'Leave')}</em>`}</span>`).join('');
      return `<div class="mm-srow${skip ? ' is-off' : ''}" ${skip ? '' : `draggable="true" tabindex="0" data-uid="${esc(p.userId)}" aria-label="${esc(p.name)} — Alt and the arrow keys move them"`}>
        <span class="mm-grip" aria-hidden="true">${skip ? '' : '⋮⋮'}</span>
        <span class="mm-who">${av(p.initials, skip ? 'off' : '')}<b>${esc(p.name)}</b><span class="mm-role">${esc(skip ? 'On leave · skipped' : p.role)}</span></span>
        <span class="mm-sdays">${days}</span>
        <span class="mm-n">${skip ? '–' : liveN || '–'}</span><span class="mm-n">${open.length || '–'}</span>
        <span class="mm-n${late ? ' late' : ''}">${skip ? '–' : late || '–'}</span><span class="mm-n mv">${skip || mv == null ? '–' : mv || '–'}</span>
      </div>`;
    };
    const flt = fromLastTime(last, tasks, today);
    const remoteNames = remote ? remote.ids.map((id) => firstName(userName(id))).filter(Boolean) : [];
    const resumeOther = live.find((m) => String(m.runnerEmail || '').toLowerCase() !== String(session.email || '').toLowerCase());
    root.innerHTML = `
      <div class="mm-top"><span class="mm-word">Track<span class="mm-dot">.</span></span>
        <span class="mm-title"><b>Team meeting</b> · not started</span><span class="mm-grow"></span>
        <button type="button" class="mm-btn mm-close" data-a="close">Close ${kbd('Esc')}</button></div>
      <div class="mm-start">
        <div class="mm-start-main">
          <h1 class="mm-h1 arrive-head">Team meeting</h1>
          <div class="mm-sub">${esc(longDate(now))} · ${esc(hhmm(now))} · ${esc(plural(inOrder.length, 'person', 'people'))}, about ${esc(String(inOrder.length * S.minutes))} minutes</div>
          <div class="mm-stable">
            <div class="mm-shead"><span></span><span>Person · in this order</span><span class="mm-sdays">${head}</span><span>Live</span><span>Tasks</span><span>Late</span><span>Moved</span></div>
            <div class="mm-slist arrive">${inOrder.map((p) => row(p, false)).join('')}${skipped.map((p) => row(p, true)).join('')}
              <div class="mm-srow is-none"><span class="mm-grip"></span><span class="mm-who">${av('?', 'none')}<b>No owner</b><span class="mm-role">Taken at the end</span></span>
                <span class="mm-sdays"></span><span class="mm-n">${unled || '–'}</span><span class="mm-n">${unowned || '–'}</span><span class="mm-n">–</span><span class="mm-n">–</span></div>
            </div>
          </div>
          ${notIn.length ? `<div class="mm-notin" aria-label="Not in this meeting"><span class="mm-notin-lab">Not in this meeting</span>
            ${notIn.map((p) => `<span class="mm-notin-who">${esc(p.name)} <button type="button" class="mm-btn sm" data-add="${esc(p.userId)}" aria-label="Add ${esc(p.name)} to this meeting">Add</button></span>`).join('')}
            <span class="mm-help">Their timesheets aren’t required and they have no live project or open task.</span></div>` : ''}
          <p class="mm-foot-note">Drag to change the order.${last ? ` “Moved” counts what has changed on their work since ${esc(lastDay)}’s meeting.` : ' “Moved” starts counting after the first meeting.'}</p>
        </div>
        <aside class="mm-panel">
          ${resumeOther ? `<div class="mm-other">${esc(firstName(resumeOther.runnerName || resumeOther.runnerEmail))} is running a meeting.
            <button type="button" class="mm-btn sm" data-a="follow-other">Follow it</button></div>` : ''}
          <div class="mm-lab">Run it as</div>
          <div class="mm-seg" role="group" aria-label="Run it as">
            <button type="button" data-mode="walkthrough" class="${S.mode === 'walkthrough' ? 'on' : ''}" aria-pressed="${S.mode === 'walkthrough'}">Walkthrough</button>
            <button type="button" data-mode="standup" class="${S.mode === 'standup' ? 'on' : ''}" aria-pressed="${S.mode === 'standup'}">Standup</button>
          </div>
          <div class="mm-help">Projects and tasks, person by person. Standup is tasks only.</div>
          <label class="mm-lab" for="mm-order">Order</label>
          <div class="mm-select"><select id="mm-order">${ORDER_PRESETS.filter((k) => k !== 'last' || (last && last.order)).map((k) => `<option value="${k}"${S.preset === k ? ' selected' : ''}>${esc(presetLabel(k, remote))}</option>`).join('')}</select>${navIcon('chevronDown', 14)}</div>
          <div class="mm-lab">Time each</div>
          <div class="mm-time"><div class="mm-step"><button type="button" data-a="less" aria-label="Less time each">−</button><span>${esc(String(S.minutes))} min</span><button type="button" data-a="more" aria-label="More time each">+</button></div>
            <span class="mm-help">A ring counts down. Nobody gets cut off.</span></div>
          ${room ? `<label class="mm-toggle"><input type="checkbox" data-a="room"${S.roomFollows ? ' checked' : ''}><span class="mm-sw" aria-hidden="true"></span>
            <span><b>Room screen follows</b><span class="mm-help">People, projects and dates. No fees, hours or contacts.</span></span></label>` : ''}
          ${remote ? `<label class="mm-toggle"><input type="checkbox" data-a="remote"${S.remoteFollow ? ' checked' : ''}><span class="mm-sw" aria-hidden="true"></span>
            <span><b>${esc(remote.label)} can follow in Track</b><span class="mm-help">${esc(listNames(remoteNames))} get${remoteNames.length === 1 ? 's' : ''} a Follow button on their screen.</span></span></label>` : ''}
          ${flt ? `<div class="mm-last"><div class="mm-last-h"><span>From last time · ${esc(longDayDm(flt.when))}</span><span>${esc(`${flt.done} of ${flt.total} done`)}</span></div>
            ${flt.open.slice(0, 3).map((t) => `<div class="mm-last-row"><div><div>${esc(t.title)}</div><div class="mm-meta">${esc([firstName(userName(t.assigneeId)), projectsById[t.projectId] && projectsById[t.projectId].number].filter(Boolean).join(' · '))}</div></div>
              ${t.lateDays ? `<span class="late">${esc(plural(t.lateDays, 'day'))}</span>` : ''}</div>`).join('')}</div>` : ''}
          <button type="button" class="mm-go" data-a="start"${inOrder.length ? '' : ' disabled'}>Start meeting ${kbd('Enter')}</button>
        </aside>
      </div>`;
    wireStart();
  }
  function listNames(names) {
    if (names.length <= 1) return names.join('');
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  }
  function wireStart() {
    root.querySelector('[data-a="close"]').addEventListener('click', () => close());
    root.querySelectorAll('[data-mode]').forEach((b) => b.addEventListener('click', () => { S.mode = b.dataset.mode; drawStart(); }));
    root.querySelector('#mm-order').addEventListener('change', (ev) => {
      S.preset = ev.target.value;
      S.order = orderPeople(inMeeting(), S.preset, { live: liveOf, meId: me && me.userId, remote, lastOrder: last && last.order });
      drawStart();
    });
    root.querySelector('[data-a="less"]').addEventListener('click', () => { S.minutes = Math.max(1, S.minutes - 1); drawStart(); });
    root.querySelector('[data-a="more"]').addEventListener('click', () => { S.minutes = Math.min(10, S.minutes + 1); drawStart(); });
    root.querySelector('[data-a="room"]')?.addEventListener('change', (ev) => { S.roomFollows = ev.target.checked; });
    root.querySelector('[data-a="remote"]')?.addEventListener('change', (ev) => { S.remoteFollow = ev.target.checked; });
    root.querySelector('[data-a="start"]').addEventListener('click', () => start());
    // Add someone left out — for this meeting only (the order goes on its record).
    root.querySelectorAll('[data-add]').forEach((b) => b.addEventListener('click', () => {
      const id = b.dataset.add;
      S.out.delete(id);
      if (!S.order.includes(id) && !away().has(id)) S.order.push(id);
      drawStart();
      root.querySelector(`.mm-srow[data-uid="${CSS.escape(id)}"]`)?.focus();
    }));
    root.querySelector('[data-a="follow-other"]')?.addEventListener('click', () => {
      const m = live.find((x) => String(x.runnerEmail || '').toLowerCase() !== String(session.email || '').toLowerCase());
      if (m) startFollowing(m);
    });
    // Drag to change the order.
    let dragId = null;
    root.querySelectorAll('.mm-srow[data-uid]').forEach((r) => {
      r.addEventListener('dragstart', (ev) => { dragId = r.dataset.uid; r.classList.add('is-drag'); ev.dataTransfer.effectAllowed = 'move'; try { ev.dataTransfer.setData('text/plain', dragId); } catch (_) { /* old browsers */ } });
      r.addEventListener('dragend', () => { r.classList.remove('is-drag'); dragId = null; });
      r.addEventListener('dragover', (ev) => { if (dragId && dragId !== r.dataset.uid) { ev.preventDefault(); r.classList.add('is-over'); } });
      r.addEventListener('dragleave', () => r.classList.remove('is-over'));
      r.addEventListener('drop', (ev) => {
        ev.preventDefault(); r.classList.remove('is-over');
        if (!dragId || dragId === r.dataset.uid) return;
        const from = S.order.indexOf(dragId); const to = S.order.indexOf(r.dataset.uid);
        if (from < 0 || to < 0) return;
        S.order.splice(from, 1); S.order.splice(to, 0, dragId);
        drawStart();
      });
    });
  }
  function moveInOrder(uid, d) {
    const i = S.order.indexOf(uid); const j = i + d;
    if (i < 0 || j < 0 || j >= S.order.length) return;
    [S.order[i], S.order[j]] = [S.order[j], S.order[i]];
    drawStart();
    root.querySelector(`.mm-srow[data-uid="${CSS.escape(uid)}"]`)?.focus();
  }

  async function start() {
    if (!S.order.length || S.saving) return;
    S.saving = true;
    const at = new Date().toISOString();
    S.startedAt = at; S.personStartedAt = at; S.idx = 0; S.sel = 0; S.selPid = null;
    const rec = {
      state: 'running', mode: S.mode, order: S.order.slice(), current: S.order[0], personStartedAt: at,
      minutesEach: S.minutes, startedAt: at, endedAt: null,
      runnerId: (session && session.uid) || null, runnerEmail: session.email, runnerName: session.displayName || (me && me.name) || session.email,
      roomFollows: !!S.roomFollows, remoteFollow: !!S.remoteFollow,
      actionTaskIds: [], changes: [], noteIds: [], followers: {}, updatedAt: at,
    };
    if (S.roomFollows) rec.room = roomFor();
    try {
      const m = await createMeeting(rec);
      S.meeting = m && m.id ? m : null;
    } catch (e) { S.meeting = null; }
    if (!S.meeting) toast('This meeting is not being saved, so the room screen and other offices can’t follow it. Everything you change still saves.');
    S.saving = false;
    beginRun();
  }

  function resume(m) {
    S.meeting = m; S.mode = m.mode || 'walkthrough'; S.order = (m.order || []).filter((id) => personOf(id));
    S.minutes = m.minutesEach || 3; S.roomFollows = !!m.roomFollows; S.remoteFollow = !!m.remoteFollow;
    S.startedAt = m.startedAt; S.personStartedAt = m.personStartedAt || m.startedAt;
    S.actionIds = Array.isArray(m.actionTaskIds) ? m.actionTaskIds.slice() : [];
    S.changes = Array.isArray(m.changes) ? m.changes.slice() : [];
    S.notes = (Array.isArray(m.noteIds) ? m.noteIds : []).map((n) => ({ ...n, text: (notes.find((x) => x.id === n.id) || {}).text || '' }));
    for (const id of S.actionIds) { const t = tasks.find((x) => x.id === id); if (t) S.made[id] = t; }
    const i = seq().indexOf(m.current);
    S.idx = i < 0 ? 0 : i;
    beginRun();
    toast('Picked up where the meeting was.');
  }

  // ══ RUN ═══════════════════════════════════════════════════════════════
  function beginRun() {
    S.phase = S.phase === 'follow' ? 'follow' : 'run';
    S.readOnly = S.phase === 'follow';
    root.innerHTML = `<div class="mm-top">${topInner()}</div><div class="mm-body"></div>`;
    wireTop();
    drawBody();
    timers.push(setInterval(tick, 1000));
    if (S.meeting && S.meeting.id && !S.readOnly) {
      // Heartbeat: the room and the followers stop following a meeting whose
      // runner has gone quiet for 15 minutes (meetingLive).
      timers.push(setInterval(() => persist({}), 5 * 60000));
      stopWatch = watchMeeting(S.meeting.id, (m) => { if (m) { S.followers = m.followers || {}; paintFollowers(); } });
    }
    watchLive();
  }

  // ── Live: changes made anywhere else while the meeting is open ─────────
  // Projects written since a little before the meeting opened (a small range
  // read, not the register) and the open tasks — the Tasks screen's own live
  // read. Both fold into the meeting's own objects; the screen redraws in place.
  function watchLive() {
    if (unsubs.length) return;
    const since = new Date(Date.parse(openedAt) - 15 * 60000).toISOString();
    const stop = (u) => { if (typeof u === 'function') unsubs.push(u); };
    try {
      stop(watchProjectsUpdatedSince(since, (rows) => {
        if (!Array.isArray(rows) || !root.isConnected) return;
        const r = mergeProjects(projects, projectsById, rows.filter((x) => x && !x.isTest));
        r.moves.forEach(noteMove);
        if (r.changed) liveChanged();
      }));
    } catch (_) { /* no listener: the meeting still shows its own changes */ }
    try {
      stop(watchTeamTasks((rows) => {
        if (!Array.isArray(rows) || !root.isConnected) return;
        if (mergeTasks(tasks, rows)) liveChanged();
      }));
    } catch (_) { /* as above */ }
  }
  // A change seen on a project goes on the meeting's own list of what moved
  // (history lines, "up from Review"), once.
  function noteMove(m) {
    const ch = { ...m };
    if (ch.field === 'lead') {
      const name = (v) => { const id = String(v || '').split('/').pop(); return id ? userName(id) : null; };
      ch.from = String(ch.from || '').includes('/') ? name(ch.from) : ch.from; ch.to = String(ch.to || '').includes('/') ? name(ch.to) : ch.to;
    }
    if (changes.some((x) => x.record === ch.record && x.field === ch.field && JSON.stringify(x.to) === JSON.stringify(ch.to) && Math.abs(Date.parse(x.at) - Date.parse(ch.at)) < 120000)) return;
    changes.push(ch);
  }
  // Redraw now when nothing is in the way; otherwise when it is (tick).
  function busy() {
    if (S.armed || S.editing || S.saving) return true;
    if (root.querySelector('.mm-pop') || document.querySelector('.pmenu, dialog[open], .tf-scrim')) return true;
    const bar = root.querySelector('.mm-bar-in');
    return !!(bar && bar.value.trim());
  }
  function liveChanged() {
    S.dirty = true;
    if (S.phase === 'run' || S.phase === 'follow') {
      if (!busy()) refresh();
      roomSoon();
    }
  }
  function refresh() {
    if (!root.querySelector('.mm-body')) return;
    const top = root.querySelector('.mm-top');
    if (top && top.querySelector('.mm-strip')) { top.innerHTML = topInner(); wireTop(); }
    drawBody({ quiet: true });
  }
  // The room screen shows a change straight away (debounced: a write and its
  // echo from the listener land as one).
  let roomTimer = null;
  function roomSoon() {
    if (S.readOnly || !S.roomFollows || !S.meeting) return;
    clearTimeout(roomTimer);
    roomTimer = setTimeout(() => { if (root.isConnected) persist({}); }, 400);
  }

  function topInner() {
    const ids = S.order;
    const c = cur();
    const pos = ids.indexOf(c);
    const strip = ids.map((id, i) => {
      const p = personOf(id); if (!p) return '';
      const state = i === pos ? 'now' : (pos >= 0 ? i < pos : c !== NOOWNER && c !== WRAP ? false : true) ? 'done' : 'next';
      const st = c === NOOWNER || c === WRAP ? 'done' : state;
      return i === pos
        ? `<button type="button" class="mm-chip now" data-go="${i}" aria-current="step">${av(p.initials)}<b>${esc(firstName(p.name))}</b><span>${i + 1} of ${ids.length}</span></button>`
        : `<button type="button" class="mm-chip ${st}" data-go="${i}" aria-label="${esc(p.name)}">${esc(p.initials)}</button>`;
    }).join('');
    const nNo = noOwnerItems().filter((x) => !S.given[x.key]).length;
    return `<span class="mm-word">Track<span class="mm-dot">.</span></span>
      <span class="mm-title"><b>Team meeting</b> · <span data-clock>${esc(hhmm())}</span></span>
      <nav class="mm-strip" aria-label="People">${strip}<span class="mm-rule" aria-hidden="true"></span>
        <button type="button" class="mm-pill${c === NOOWNER ? ' on' : c === WRAP ? ' past' : ''}" data-go="${ids.length}">No owner <span>${nNo}</span></button>
        <button type="button" class="mm-pill${c === WRAP ? ' on' : ''}" data-go="${ids.length + 1}">Wrap up</button></nav>
      <span class="mm-grow"></span>
      <span class="mm-follow" data-followers></span>
      ${S.roomFollows && S.meeting ? '<span class="mm-roomon"><i aria-hidden="true"></i>Room screen</span>' : ''}
      ${S.readOnly ? `<span class="mm-roomon">Following ${esc(firstName(S.meeting && S.meeting.runnerName))}</span>` : ''}
      <button type="button" class="mm-btn mm-close" data-a="close">${S.readOnly ? 'Leave' : 'Close'} ${kbd('Esc')}</button>`;
  }
  function wireTop() {
    const top = root.querySelector('.mm-top');
    top.querySelector('[data-a="close"]').addEventListener('click', () => close());
    if (!S.readOnly) top.querySelectorAll('[data-go]').forEach((b) => b.addEventListener('click', () => go(Number(b.dataset.go))));
    paintFollowers();
  }
  function paintFollowers() {
    const host = root.querySelector('[data-followers]'); if (!host) return;
    const f = Object.values(S.followers || {}).filter(Boolean);
    host.innerHTML = f.length ? `<span class="mm-stack">${f.slice(0, 4).map((x) => av(x.initials || initialsOf(x.name), 'xs')).join('')}</span>following` : '';
  }
  function tick() {
    const clock = root.querySelector('[data-clock]'); if (clock) clock.textContent = hhmm();
    paintRing();
    if (S.dirty && !busy()) refresh();
  }
  function ringSvg(left, total) {
    const frac = Math.max(0, Math.min(1, left / total));
    const C = 2 * Math.PI * 21;
    return `<svg viewBox="0 0 48 48" class="mm-ring-svg${left < 0 ? ' over' : ''}" aria-hidden="true"><circle cx="24" cy="24" r="21" class="mm-ring-bg"/>
      <circle cx="24" cy="24" r="21" class="mm-ring-fg" stroke-dasharray="${C.toFixed(2)}" stroke-dashoffset="${(C * (1 - frac)).toFixed(2)}" transform="rotate(-90 24 24)"/></svg>`;
  }
  function paintRing() {
    const host = root.querySelector('[data-ring]'); if (!host) return;
    const startAt = S.readOnly && S.meeting ? S.meeting.personStartedAt : S.personStartedAt;
    const mins = S.readOnly && S.meeting ? S.meeting.minutesEach : S.minutes;
    const left = ringLeft(startAt, mins);
    host.innerHTML = `${ringSvg(left, mins * 60)}<span class="mm-ring-t"><b class="${left < 0 ? 'over' : ''}">${esc(ringText(left))}</b><span>of ${esc(String(mins))} min</span></span>`;
  }

  // Every write carries the room snapshot as it now stands, so a stage, a date,
  // a lead or an action shows on the room screen with the change itself — it
  // used to be written only on Next, so the room showed the old stage.
  async function persist(fields) {
    if (!S.meeting || !S.meeting.id || S.readOnly) return;
    const f = { ...fields };
    if (S.roomFollows && !('room' in f) && S.phase === 'run') { try { f.room = roomFor(); } catch (_) { /* the change still saves */ } }
    clearTimeout(roomTimer);
    try { await updateMeeting(S.meeting.id, { ...f, updatedAt: new Date().toISOString() }); } catch (e) { /* the meeting goes on; the room may lag */ }
  }

  function go(i) {
    const n = seq().length;
    if (S.readOnly || i < 0 || i >= n || i === S.idx) return;
    const body = root.querySelector('.mm-body');
    S.idx = i; S.sel = 0; S.selPid = null; S.nsel = 0; S.armed = null; S.editing = null; S.barProject = null; S.noDate = false;
    S.personStartedAt = new Date().toISOString();
    if (body) body.classList.add('is-leaving');
    setTimeout(() => {
      const top = root.querySelector('.mm-top');
      top.innerHTML = topInner(); wireTop();
      drawBody();
    }, 150);
    const fields = { current: cur(), personStartedAt: S.personStartedAt };
    if (S.roomFollows) fields.room = roomFor();
    persist(fields);
  }

  // quiet: a redraw in place (a change, a live update) — no arrival motion, the
  // scroll and the action bar's focus kept.
  function drawBody({ quiet = false } = {}) {
    const body = root.querySelector('.mm-body'); if (!body) return;
    body.classList.remove('is-leaving');
    const c = cur();
    const scroll = body.scrollTop;
    const barFocused = quiet && document.activeElement && document.activeElement.classList && document.activeElement.classList.contains('mm-bar-in');
    S.dirty = false;
    body.className = `mm-body${quiet ? '' : ' mm-in'} ${c === NOOWNER ? 'is-noowner' : c === WRAP ? 'is-wrap' : 'is-person'}`;
    if (c === NOOWNER) body.innerHTML = noOwnerHtml();
    else if (c === WRAP) body.innerHTML = wrapHtml();
    else body.innerHTML = personHtml(c);
    if (quiet) {
      body.querySelectorAll('.arrive, .arrive-head').forEach((e) => e.classList.remove('arrive', 'arrive-head'));
      body.scrollTop = scroll;
    }
    wireBody(body, c);
    if (barFocused) body.querySelector('.mm-bar-in')?.focus();
    paintRing();
    const n = root.querySelector('.mm-strip .mm-pill span');
    if (n) n.textContent = String(noOwnerItems().filter((x) => !S.given[x.key]).length);
  }

  // ══ 2. A PERSON ═══════════════════════════════════════════════════════
  function personVm(id) {
    const p = personOf(id) || { userId: id, name: userName(id), initials: initialsOf(userName(id)), role: '' };
    const led = projectsLedBy(projects, id);
    const open = openTasksOf(id);
    const overdue = open.filter((t) => isLateTask(t, today)).length;
    const week = weekStrip(p, { leave, tasks: tasks.concat(done), projectsById, today });
    const rows = led.map((x) => {
      const s = projectSince(x, { changes, tasksDone: done, notes, tasksMade: tasks, since });
      const mv = movedLine(x, s, { emails: emails[x.number] || 0 });
      const due = x.due ? String(x.due).slice(0, 10) : '';
      const late = !!due && due < today && !['Issued', 'Complete'].includes(x.status);
      const next = nextActionFor(x, tasks.filter((t) => t.projectId === x.id && isOpenTask(t)), { today: new Date(), overdue: late, firstName: (uid) => firstName(userName(uid)) });
      return { p: x, s, moved: mv, due, late, next };
    });
    const checks = tasks.filter((t) => isOpenTask(t) && t.checkerId === id && t.assigneeId !== id && t.state === 'in_review').length;
    return { person: p, rows, open, overdue, week, groups: groupTasks(open, today), checks };
  }

  function personHtml(id) {
    const vm = personVm(id);
    const { person, rows, open, overdue, week } = vm;
    const walk = S.mode === 'walkthrough';
    const sub = `${person.role ? `${person.role} · ` : ''}${walk ? `${plural(rows.length, 'live project')} · ` : ''}${plural(open.length, 'open task')}`;
    const lastWhen = last ? `Since the last meeting, ${longDayDm(last.startedAt)}` : 'In the last seven days';
    const prevName = S.idx > 0 ? labelOf(seq()[S.idx - 1]) : '';
    const nextName = labelOf(seq()[S.idx + 1], true);
    pinSel(id);
    const stripHtml = week.map((d) => `<span class="mm-day${d.today ? ' today' : ''} ${d.kind}"><span>${esc(`${d.dow} ${d.date}`)}</span><b>${esc(d.label)}</b></span>`).join('');
    return `
      <header class="mm-phead">
        ${av(person.initials, 'lg')}
        <div class="mm-pname"><h1 class="mm-h1">${esc(person.name)}</h1>
          <div class="mm-sub">${esc(sub)}${overdue ? `, <span class="late">${esc(`${overdue} overdue`)}</span>` : ''}</div></div>
        <span class="mm-grow"></span>
        <div class="mm-week" aria-label="This week">${stripHtml}</div>
        <div class="mm-ring" data-ring role="timer" aria-label="Time for this person"></div>
      </header>
      <div class="mm-cols${walk ? '' : ' standup'}">
        ${walk ? `<section class="mm-projects" aria-label="Projects">
          <div class="mm-sec-h"><h2>Projects <span>${esc(`${rows.length} live`)}</span></h2><span class="mm-since"><i aria-hidden="true"></i>${esc(lastWhen)}</span></div>
          ${rows.length ? `<div class="mm-thead"><span>No</span><span>Project</span><span>Stage</span><span>Next</span><span>Due</span></div>
          <div class="mm-plist arrive">${rows.map((r, i) => projectRow(r, i === S.sel)).join('')}</div>`
    : `<div class="mm-empty">${esc(firstName(person.name))} leads no live projects.</div>`}
        </section>` : ''}
        <section class="mm-tasks" aria-label="Tasks">
          <div class="mm-sec-h"><h2>Tasks <span>${esc(`${open.length} open`)}</span></h2><span class="mm-checks">${vm.checks ? esc(`${plural(vm.checks, 'check')} owed`) : 'No checks owed'}</span></div>
          <div class="mm-tlist">${tasksHtml(vm.groups)}</div>
        </section>
      </div>
      ${S.readOnly ? '' : actionBarHtml(person)}
      <footer class="mm-foot">
        ${prevName && !S.readOnly ? `<button type="button" class="mm-btn" data-a="prev">${navIcon('chevronLeft', 14)} ${esc(prevName)}</button>` : ''}
        ${S.readOnly ? `<span class="mm-keys">This screen moves with ${esc(firstName(S.meeting && S.meeting.runnerName) || 'the meeting')}. Next: ${esc(nextName)}</span>` : `<button type="button" class="mm-btn primary" data-a="next">Next: ${esc(nextName)} ${navIcon('chevronRight', 14)}</button>`}
        ${S.hint && !S.readOnly && S.idx === 0 ? '<span class="mm-hint"><i aria-hidden="true"></i>Type it as you talk. Enter puts it on their list. Then the next person.</span>' : ''}
        <span class="mm-grow"></span>
        ${S.readOnly ? '' : `<span class="mm-keys">${kbd('←')}${kbd('→')} people ${walk ? `<span class="mm-gap"></span>${kbd('↑')}${kbd('↓')} projects` : ''}</span>`}
        ${S.readOnly ? '' : thisMeetingBtn()}
      </footer>`;
  }
  function labelOf(id, full = false) {
    if (id === NOOWNER) return 'No owner';
    if (id === WRAP) return 'Wrap up';
    if (!id) return '';
    const n = userName(id);
    return full ? n : firstName(n);
  }
  function stagePill(p, selected) {
    const cls = p.status === 'Ready to Issue' || p.status === 'Part Issued' ? 'hi' : p.status === 'Not Started' ? 'lo' : '';
    return `<span class="mm-stage ${cls}${selected ? ' sel' : ''}">${esc(stageText(p.status))}${S.readOnly ? '' : navIcon('chevronDown', 12)}</span>`;
  }
  function projectRow(r, selected) {
    const { p, s, moved, due, late, next } = r;
    const hist = selected ? historyLines(s, { name: nameByEmail }) : [];
    const openT = selected ? tasks.filter((t) => t.projectId === p.id && isOpenTask(t)).sort((a, b) => String(a.dueDate || '9').localeCompare(String(b.dueDate || '9')))[0] : null;
    return `<div class="mm-prow${selected ? ' sel' : ''}" data-pid="${esc(p.id)}">
      <div class="mm-prow-main">
        <span class="mm-no">${esc(p.number || '—')}</span>
        <span class="mm-pj"><b>${esc(clientOf(p))}</b><span>${esc(p.title || p.service || '')}</span></span>
        <span class="mm-st">${stagePill(p, selected)}${moved.text ? `<span class="mm-moved ${moved.tone}">${esc(moved.text)}</span>` : ''}</span>
        <span class="mm-next${next && next.late ? ' late' : ''}">${esc(next ? next.text : '—')}</span>
        <span class="mm-due${late ? ' late' : ''}">${esc(due ? dm(due) : '—')}</span>
      </div>
      ${selected ? `<div class="mm-prow-more">
        <div class="mm-hist">${hist.slice(0, 5).map((h) => `<div><span>${esc(h.when)}</span><span>${esc(h.text)}</span></div>`).join('')}
          ${openT ? `<div><span>Open</span><span>${esc(openT.title)}${openT.dueDate ? ` · ${esc(dowDm(openT.dueDate))}` : ''}</span></div>` : ''}
          ${!hist.length && !openT ? `<div><span></span><span class="mm-quiet">Nothing on record ${last ? 'since the last meeting' : 'in the last seven days'}.</span></div>` : ''}</div>
        ${S.readOnly ? '' : `<div class="mm-row-acts">
          ${rowBtn('stage', 'Stage', 'S', canEdit, true)}${rowBtn('date', 'New date', 'D', canEdit)}${rowBtn('action', 'Action', 'A', true)}${rowBtn('note', 'Note', 'N', true)}</div>`}
        ${S.readOnly ? '' : editorHtml(p)}
      </div>` : ''}
    </div>`;
  }
  function rowBtn(kind, label, key, allowed, chev = false) {
    return `<button type="button" class="mm-btn sm" data-row-act="${kind}"${allowed ? '' : ' aria-disabled="true" title="Reviewer roles change this"'}>${esc(label)}${chev ? navIcon('chevronDown', 12) : ''} ${kbd(key)}</button>`;
  }
  function editorHtml(p) {
    const a = S.armed; const e = S.editing;
    if (a && a.kind === 'stage' && a.pid === p.id) {
      return `<div class="mm-confirm" role="alert"><span>Move ${esc(p.number || 'this project')} from ${esc(stageText(p.status))} to <b>${esc(stageText(a.to))}</b>?</span>
        <button type="button" class="mm-btn sm primary" data-ed="stage-yes">Move it ${kbd('S')}</button><button type="button" class="mm-btn sm" data-ed="cancel">Not now</button>
        <span class="mm-help">Stages only move forward. It is logged.</span></div>`;
    }
    if (e && e.pid === p.id && e.kind === 'date') {
      return `<div class="mm-editor"><label class="mm-lab" for="mm-date">New date for ${esc(p.number || '')}</label>
        <input id="mm-date" type="date" class="mm-input" value="${esc(p.due ? String(p.due).slice(0, 10) : '')}">
        <button type="button" class="mm-btn sm primary" data-ed="date-save">Save date</button><button type="button" class="mm-btn sm" data-ed="cancel">Cancel</button></div>`;
    }
    if (e && e.pid === p.id && e.kind === 'note') {
      return `<div class="mm-editor"><label class="mm-lab" for="mm-note">Note on ${esc(p.number || '')} — everyone on the project sees it</label>
        <input id="mm-note" type="text" class="mm-input wide" maxlength="600" placeholder="Hold the steel order until the revised drawings arrive">
        <button type="button" class="mm-btn sm primary" data-ed="note-save">Save note</button><button type="button" class="mm-btn sm" data-ed="cancel">Cancel</button></div>`;
    }
    return '';
  }
  function tasksHtml(groups) {
    if (!groups.length) return '<div class="mm-empty">Nothing open.</div>';
    const fromLast = new Set((last && last.actionTaskIds) || []);
    return groups.map((g) => {
      const fold = g.key === 'nodate' && !S.noDate;
      return `<div class="mm-tg ${g.key}"><div class="mm-tg-h"><span>${esc(g.label)}</span>${fold
        ? `<button type="button" class="mm-link" data-a="nodate" aria-expanded="false">${esc(String(g.tasks.length))} ${navIcon('chevronRight', 12)}</button>`
        : `<span>${esc(String(g.tasks.length))}</span>`}</div>
        ${fold ? '' : `<div class="mm-tg-list arrive">${g.tasks.map((t) => {
          const p = projectsById[t.projectId];
          const just = !!S.made[t.id] && S.made[t.id]._justNow;
          const meta = g.key === 'held' ? `${p ? `${p.number} · ` : ''}${heldText(t, today)}` : (p ? `${p.number} ${clientOf(p)}` : 'Overhead');
          return `<div class="mm-task${just ? ' just' : ''}" data-tid="${esc(t.id)}"><span class="mm-tick" aria-hidden="true"></span>
            <span class="mm-tt"><span>${esc(t.title)}${fromLast.has(t.id) && last ? `<span class="mm-tag">From ${esc(weekdayName(last.startedAt))}</span>` : ''}</span>
              <span class="mm-meta">${esc(meta)}${just ? ' · added just now' : ''}</span></span>
            <span class="mm-tw">${esc(taskWhen(t, today))}</span></div>`;
        }).join('')}</div>`}</div>`;
    }).join('');
  }
  function thisMeetingBtn() {
    const a = S.actionIds.length; const c = S.changes.filter((x) => x.kind !== 'owner').length;
    return `<button type="button" class="mm-btn mm-this" data-a="this" aria-haspopup="true">This meeting · <b>${a}</b> ${a === 1 ? 'action' : 'actions'}${c ? ` · <b>${c}</b> ${c === 1 ? 'change' : 'changes'}` : ''} ${navIcon('chevronDown', 12)}</button>`;
  }

  // ── 3. The action bar ──────────────────────────────────────────────────
  function actionBarHtml(person) {
    return `<div class="mm-bar" data-bar>
      <span class="mm-plus" aria-hidden="true">+</span>
      <div class="mm-bar-field"><div class="mm-mirror" aria-hidden="true"></div>
        <input type="text" class="mm-bar-in" aria-label="Add an action for ${esc(firstName(person.name))}" placeholder="Add an action for ${esc(firstName(person.name))}" autocomplete="off" spellcheck="false"></div>
      <span class="mm-chips" aria-live="polite"></span>
      <span class="mm-bar-hint">${'<b>8563</b> files it · <b>Mon</b> dates it · <b>@</b> gives it to someone else'.replace('8563', esc(sampleNo()))} ${kbd('A')}</span>
    </div>`;
  }
  function sampleNo() { const p = projects.find((x) => isLive(x) && x.number); return (p && p.number) || '1234'; }
  function parseBar(text) {
    const ctx = {
      today: new Date(),
      projects: projects.filter((p) => p.number).map((p) => ({ id: p.id, number: String(p.number), ref: String(p.number), name: clientOf(p), clientName: p.clientName })),
      people: people.map((p) => ({ id: p.userId, name: p.name, email: p.email })),
    };
    return parseTaskLine(text, ctx);
  }
  function paintBar(bar) {
    const input = bar.querySelector('.mm-bar-in');
    const mirror = bar.querySelector('.mm-mirror');
    const chipsEl = bar.querySelector('.mm-chips');
    const text = input.value;
    const parsed = parseBar(text);
    if (!parsed.project && S.barProject) parsed.project = { id: S.barProject.id, name: clientOf(S.barProject) };
    // The mirror draws the typed line with its matched words underlined in blue.
    let html = ''; let at = 0;
    for (const sp of parsed.spans) { html += esc(text.slice(at, sp.start)) + `<u>${esc(text.slice(sp.start, sp.end))}</u>`; at = sp.end; }
    mirror.innerHTML = html + esc(text.slice(at));
    mirror.scrollLeft = input.scrollLeft;
    const c = cur();
    const person = c && c !== NOOWNER && c !== WRAP ? personOf(c) : null;
    const chips = actionChips(parsed, { person: person ? { name: person.name, initials: person.initials } : null, projectsById });
    bar.classList.toggle('has-text', !!text.trim());
    chipsEl.innerHTML = text.trim() ? chips.map((ch) => (ch.kind === 'person' ? `<span class="mm-cp">${av(ch.initials, 'xs')}${esc(ch.label)}</span>`
      : ch.kind === 'project' ? `<span class="mm-cp"><b>${esc(ch.no)}</b>${esc(ch.label)}</span>`
        : ch.kind === 'ask' ? `<span class="mm-cp ask">${esc(ch.label)}</span>` : `<span class="mm-cp">${esc(ch.label)}</span>`)).join('')
      + (parsed.title ? kbd('Enter') : '') : (S.barProject ? `<span class="mm-cp"><b>${esc(S.barProject.number || '')}</b>${esc(clientOf(S.barProject))}</span>` : '');
    return parsed;
  }
  function focusBar(project) {
    const bar = root.querySelector('[data-bar]'); if (!bar) return;
    S.barProject = project || null;
    const input = bar.querySelector('.mm-bar-in');
    input.focus();
    paintBar(bar);
  }
  async function saveAction(bar) {
    const input = bar.querySelector('.mm-bar-in');
    const parsed = paintBar(bar);
    if (!parsed.title || (parsed.ambiguous && parsed.ambiguous.kind === 'project')) return;
    const c = cur();
    const said = parsed.owner ? personOf(parsed.owner.id) : (c && c !== NOOWNER && c !== WRAP ? personOf(c) : null);
    const owner = said && said.email ? said : null;
    const fields = {
      title: parsed.title, projectId: parsed.project ? parsed.project.id : null,
      assigneeId: owner ? owner.userId : null, assigneeEmail: owner ? owner.email : null,
      dueDate: parsed.dueDate || null, priority: 2, source: 'manual',
      sourceRef: S.meeting && S.meeting.id ? `meetings/${S.meeting.id}` : null, sourceLabel: 'Team meeting',
    };
    input.disabled = true;
    try {
      const t = await createTask(fields, session);
      const id = (t && t.id) || `local-${Date.now()}`;
      const task = { ...fields, state: 'todo', id, createdAt: new Date().toISOString(), plannedDate: null, _justNow: true };
      tasks.push(task); S.made[id] = task; S.actionIds.push(id);
      S.hint = false; S.barProject = null;
      if (said && !owner) toast(`Saved with nobody on it — ${firstName(said.name)} has no email on Track, so it waits in No owner.`);
      persist({ actionTaskIds: S.actionIds.slice() });
      drawBody({ quiet: true });
      setTimeout(() => { if (S.made[id]) S.made[id]._justNow = false; }, 2400);
      root.querySelector('.mm-bar-in')?.focus();
    } catch (e) {
      input.disabled = false;
      toast(`The action was not saved — ${e && e.message ? e.message : e}`);
    }
  }

  // ── Row actions: stage, new date, note ─────────────────────────────────
  function selectedProject() {
    const c = cur(); if (!c || c === NOOWNER || c === WRAP) return null;
    return pinSel(c)[S.sel] || null;
  }
  async function confirmStage(p, to) {
    const from = p.status;
    try {
      await o.applyStatus(p, to);
      const ch = { projectId: p.id, projectNo: p.number || '', kind: 'stage', from: from || null, to, at: new Date().toISOString() };
      S.changes.push(ch);
      changes.push({ record: `projects/${p.id}`, field: 'status', from, to, at: ch.at, by: session.email, byName: session.displayName || '' });
      persist({ changes: S.changes.slice() });
      toast(`${p.number || 'Project'} moved to ${stageText(to)}`);
    } catch (e) { toast(`The stage did not change — ${e && e.message ? e.message : e}`); }
    S.armed = null;
    drawBody({ quiet: true });   // the row keeps its place (pinSel); past Part issued, the next row is selected
  }
  async function saveDate(p, value) {
    const from = p.due ? String(p.due).slice(0, 10) : null;
    const to = value || null;
    if (from === to) { S.editing = null; drawBody(); return; }
    const at = new Date().toISOString();
    try {
      // The project edit panel's own write: the field, the override flag, and a
      // line on the record's change list.
      await updateProjectFields(p.id, { due: to, manualOverride: true, updatedBy: session.email, updatedAt: at });
      addRecordChange({ record: `projects/${p.id}`, field: 'due', label: 'Deadline', from, to, by: session.email, byName: session.displayName || null, at, money: false }).catch(() => {});
      p.due = to;
      S.changes.push({ projectId: p.id, projectNo: p.number || '', kind: 'due', from, to, at });
      changes.push({ record: `projects/${p.id}`, field: 'due', from, to, at, by: session.email, byName: session.displayName || '' });
      persist({ changes: S.changes.slice() });
    } catch (e) { toast(`The date was not saved — ${e && e.message ? e.message : e}`); }
    S.editing = null; drawBody({ quiet: true });
  }
  async function saveNote(p, text) {
    const t = String(text || '').trim();
    if (!t) return;
    const at = new Date().toISOString();
    try {
      const ref = await addNote({
        text: t, userName: session.displayName || (me && me.name) || session.email, userEmail: session.email,
        projectNo: String(p.number || ''), projectId: p.id, addedFrom: 'the team meeting', createdAt: at,
      });
      const id = (ref && ref.id) || `local-${Date.now()}`;
      S.notes.push({ id, projectNo: String(p.number || ''), text: t, by: session.displayName || session.email });
      notes.push({ id, projectId: p.id, projectNo: String(p.number || ''), text: t, userName: session.displayName || '', userEmail: session.email, createdAt: at });
      persist({ noteIds: S.notes.map((n) => ({ id: n.id, projectNo: n.projectNo })) });
    } catch (e) { toast(`The note was not saved — ${e && e.message ? e.message : e}`); }
    S.editing = null; drawBody({ quiet: true });
  }

  // ══ 4. NO OWNER ═══════════════════════════════════════════════════════
  // The list is frozen the first time it is drawn, so a row that has just been
  // given stays where it was (with its Undo) instead of jumping.
  function noOwnerItems() {
    const live = unownedTasks(tasks).map((x) => ({ key: `t:${x.id}`, kind: 'task', task: x }))
      .concat(unledProjects(projects).map((x) => ({ key: `p:${x.id}`, kind: 'project', project: x })));
    if (!S.noKeys) S.noKeys = live.map((x) => x.key);
    for (const x of live) if (!S.noKeys.includes(x.key)) S.noKeys.push(x.key);
    const all = S.noKeys.map((k) => (k.startsWith('t:')
      ? (() => { const t = tasks.find((x) => x.id === k.slice(2)); return t ? { key: k, kind: 'task', task: t } : null; })()
      : (() => { const p = projectsById[k.slice(2)]; return p ? { key: k, kind: 'project', project: p } : null; })()))
      .filter(Boolean)
      .filter((x) => S.given[x.key] || live.some((y) => y.key === x.key));
    return all.filter((x) => x.kind === 'task').concat(all.filter((x) => x.kind === 'project'));
  }
  function noOwnerHtml() {
    const items = noOwnerItems();
    const off = away();
    const faces = S.order.map(personOf).filter(Boolean).concat(people.filter((p) => off.has(p.userId)));
    const counts = Object.fromEntries(people.map((p) => [p.userId, openTasksOf(p.userId).length]));
    const open = items.filter((x) => !S.given[x.key]);
    if (S.nsel >= items.length) S.nsel = Math.max(0, items.length - 1);
    const focus = items[S.nsel];
    const pool = S.order.map(personOf).filter((p) => p && (focus && focus.kind === 'project' ? true : !!p.email)).map((p) => ({ ...p, role: p.sysRole }));
    const sugg = focus && !S.given[focus.key] ? suggestOwner(pool, counts, off) : null;
    const nextDay = last ? weekdayName(last.startedAt) : 'next time';
    const row = (x, i) => {
      const g = S.given[x.key];
      let no = '–'; let title = ''; let meta = '';
      if (x.kind === 'task') {
        const t = x.task; const p = projectsById[t.projectId];
        no = p ? p.number : '–';
        title = t.title;
        const raised = t.sourceLabel === 'EMAIL' ? 'raised from an email' : t.sourceLabel === 'ENQUIRY' ? 'raised from an enquiry'
          : t.createdBy ? `raised by ${nameByEmail(t.createdBy)}` : '';
        meta = [p ? clientOf(p) : 'Overhead', t.dueDate ? `due ${dowDm(t.dueDate)}` : 'no date', raised].filter(Boolean).join(' · ');
      } else {
        const p = x.project; no = p.number || '–';
        title = [clientOf(p), p.title].filter(Boolean).join(' — ');
        const fresh = p.createdAt && (Date.now() - Date.parse(p.createdAt)) < 7 * 86400000;
        meta = [fresh ? 'New this week' : '', stageLabel(p.status), p.due ? `due ${dm(p.due)}` : ''].filter(Boolean).join(' · ');
      }
      return `<div class="mm-nrow${i === S.nsel ? ' sel' : ''}" data-key="${esc(x.key)}">
        <span class="mm-no">${esc(no)}</span>
        <span class="mm-nt"><b>${esc(title)}</b><span class="mm-meta">${esc(meta)}</span>${i === S.nsel && sugg ? `<span class="mm-sugg">${esc(sugg.text)}</span>` : ''}</span>
        ${g ? `<span class="mm-given"><span class="mm-okdot" aria-hidden="true">${navIcon('check', 12)}</span>${esc(g.name)} · just now <button type="button" class="mm-link" data-undo="${esc(x.key)}">Undo</button></span>`
    : `<span class="mm-give"><span class="mm-give-l">Give it to</span>${faces.map((p) => {
      const isOff = off.has(p.userId);
      const blocked = (x.kind === 'project' && !canEdit) || (x.kind === 'task' && !p.email);
      return `<button type="button" class="mm-face${isOff ? ' off' : ''}${sugg && i === S.nsel && sugg.userId === p.userId ? ' sugg' : ''}" data-give="${esc(p.userId)}" data-key="${esc(x.key)}"${isOff || blocked ? ' disabled' : ''} aria-label="Give it to ${esc(p.name)}${isOff ? ' (on leave)' : ''}">
          ${esc(p.initials)}<small>${isOff ? 'leave' : esc(String(counts[p.userId] || 0))}</small></button>`;
    }).join('')}</span>`}
      </div>`;
    };
    const tItems = items.filter((x) => x.kind === 'task'); const pItems = items.filter((x) => x.kind === 'project');
    const prevName = labelOf(seq()[S.idx - 1]);
    return `
      <h1 class="mm-h1 arrive-head">No owner</h1>
      <div class="mm-sub">${open.length ? `${esc(word(open.length))} thing${open.length === 1 ? '' : 's'} nobody has yet. Give each one to someone, or leave it for ${esc(nextDay)}.` : 'Everything has someone on it.'}</div>
      <section class="mm-nsec"><div class="mm-sec-h ruled"><h2>Tasks with nobody on them <span>${tItems.length}</span></h2><span class="mm-help">The number under each face is their open tasks, today’s actions included</span></div>
        <div class="mm-nlist arrive">${tItems.map((x) => row(x, items.indexOf(x))).join('') || '<div class="mm-empty">None.</div>'}</div></section>
      <section class="mm-nsec"><div class="mm-sec-h ruled"><h2>Projects with no lead <span>${pItems.length}</span></h2><span class="mm-help">${canEdit ? 'A lead is who the client hears from' : 'A reviewer gives a project its lead'}</span></div>
        <div class="mm-nlist arrive">${pItems.map((x) => row(x, items.indexOf(x))).join('') || '<div class="mm-empty">None.</div>'}</div></section>
      <footer class="mm-foot">
        ${S.readOnly ? '' : `<button type="button" class="mm-btn" data-a="prev">${navIcon('chevronLeft', 14)} ${esc(prevName)}</button>
        <button type="button" class="mm-btn primary" data-a="next">Wrap up ${navIcon('chevronRight', 14)}</button>`}
        <span class="mm-grow"></span>
        ${S.readOnly ? '' : '<span class="mm-keys">Click a face to give it to them</span>'}
        ${S.readOnly ? '' : thisMeetingBtn()}
      </footer>`;
  }
  async function give(key, userId) {
    const person = personOf(userId); if (!person) return;
    const at = new Date().toISOString();
    if (key.startsWith('t:')) {
      const t = tasks.find((x) => x.id === key.slice(2)); if (!t) return;
      try {
        await assignTask(t.id, person, session);
        t.assigneeId = person.userId; t.assigneeEmail = person.email;
        S.given[key] = { name: person.name, undo: { kind: 'task', id: t.id } };
        if (!S.actionIds.includes(t.id)) S.actionIds.push(t.id);
        S.made[t.id] = t;
        persist({ actionTaskIds: S.actionIds.slice() });
      } catch (e) { toast(`Not given — ${e && e.message ? e.message : e}`); }
    } else {
      const p = projectsById[key.slice(2)]; if (!p) return;
      const prev = leadOf(p);
      try {
        await o.applyLead(p, person.userId);
        addRecordChange({ record: `projects/${p.id}`, field: 'lead', label: 'Lead', from: prev ? userName(prev) : null, to: person.name, by: session.email, byName: session.displayName || null, at, money: false }).catch(() => {});
        S.given[key] = { name: person.name, undo: { kind: 'lead', pid: p.id, prev } };
        S.changes.push({ projectId: p.id, projectNo: p.number || '', kind: 'lead', from: prev ? userName(prev) : null, to: person.name, toId: person.userId, at });
        changes.push({ record: `projects/${p.id}`, field: 'lead', from: prev ? userName(prev) : null, to: person.name, at, by: session.email, byName: session.displayName || '' });
        persist({ changes: S.changes.slice() });
      } catch (e) { toast(`Not given — ${e && e.message ? e.message : e}`); }
    }
    const items = noOwnerItems();
    const nextOpen = items.findIndex((x) => !S.given[x.key]);
    if (nextOpen >= 0) S.nsel = nextOpen;
    drawBody();
  }
  async function undoGive(key) {
    const g = S.given[key]; if (!g) return;
    try {
      if (g.undo.kind === 'task') {
        await assignTask(g.undo.id, null, session);
        const t = tasks.find((x) => x.id === g.undo.id); if (t) { t.assigneeId = null; t.assigneeEmail = null; }
        S.actionIds = S.actionIds.filter((id) => id !== g.undo.id); delete S.made[g.undo.id];
        persist({ actionTaskIds: S.actionIds.slice() });
      } else {
        const p = projectsById[g.undo.pid];
        const was = leadOf(p);
        await o.applyLead(p, g.undo.prev || '');
        addRecordChange({ record: `projects/${p.id}`, field: 'lead', label: 'Lead', from: was ? userName(was) : null, to: g.undo.prev ? userName(g.undo.prev) : null, reason: 'Undone in the team meeting', by: session.email, byName: session.displayName || null, at: new Date().toISOString(), money: false }).catch(() => {});
        const i = S.changes.map((c) => c.kind === 'lead' && c.projectId === p.id).lastIndexOf(true);
        if (i >= 0) S.changes.splice(i, 1);
        persist({ changes: S.changes.slice() });
      }
      delete S.given[key];
    } catch (e) { toast(`Could not undo — ${e && e.message ? e.message : e}`); }
    drawBody();
  }

  // ══ 5. WRAP UP ════════════════════════════════════════════════════════
  function wrapHtml() {
    const mins = Math.max(1, Math.round((Date.now() - Date.parse(S.startedAt || new Date().toISOString())) / 60000));
    const off = people.filter((p) => away().has(p.userId));
    const acts = S.actionIds.map((id) => S.made[id] || tasks.find((t) => t.id === id)).filter(Boolean);
    const ch = S.changes.filter((c) => c.kind !== 'owner');
    const mailOn = hasFeature('workspaceMailbox') && !!(S.meeting && S.meeting.id);
    const box = String(orgSetting('automationMailbox', '') || '');
    const from = box ? `${box.split('@')[0]}@` : 'Track';
    const n = S.order.length;
    const armed = S.armed && S.armed.kind === 'send';
    const chLine = (c) => {
      const p = projectsById[c.projectId];
      if (c.kind === 'stage') return { h: `${stageText(c.from)} → ${stageText(c.to)}`, s: clientOf(p) };
      if (c.kind === 'due') return { h: `Due ${c.from ? dm(c.from) : 'none'} → ${c.to ? dm(c.to) : 'none'}`, s: clientOf(p) };
      return { h: `Lead: ${c.to || 'nobody'}`, s: [clientOf(p), c.from ? `was ${c.from}` : ''].filter(Boolean).join(' · ') };
    };
    return `
      <h1 class="mm-h1 arrive-head">That’s everyone</h1>
      <div class="mm-sub">${esc(plural(mins, 'minute'))} · ${esc(plural(n, 'person', 'people'))}${off.length ? ` · ${esc(listNames(off.map((p) => firstName(p.name))))} on leave` : ''}</div>
      <div class="mm-wrap">
        <section><div class="mm-sec-h ruled"><h2>Actions <span>${acts.length}</span></h2><span class="mm-help">Already on everyone’s list</span></div>
          <div class="mm-wlist arrive">${acts.map((t) => {
            const p = projectsById[t.projectId]; const who = t.assigneeId ? personOf(t.assigneeId) : null;
            return `<div class="mm-wrow">${av(who ? who.initials : '–', 'xs')}<span><b>${esc(t.title)}</b><span class="mm-meta">${esc([who ? firstName(who.name) : 'Nobody yet', p ? `${p.number} ${clientOf(p)}` : 'Overhead'].join(' · '))}</span></span>
              <span class="mm-tw">${esc(t.dueDate ? (t.dueDate === today ? 'Today' : dowDm(t.dueDate)) : '')}</span></div>`;
          }).join('') || '<div class="mm-empty">No actions this time.</div>'}</div></section>
        <section><div class="mm-sec-h ruled"><h2>Changes <span>${ch.length}</span></h2><span class="mm-help">Saved as they were made</span></div>
          <div class="mm-wlist arrive">${ch.map((c) => { const l = chLine(c); return `<div class="mm-wrow"><span class="mm-no sm">${esc(c.projectNo || '–')}</span><span><b>${esc(l.h)}</b><span class="mm-meta">${esc(l.s)}</span></span></div>`; }).join('') || '<div class="mm-empty">Nothing changed.</div>'}</div></section>
        <section><div class="mm-sec-h ruled"><h2>Notes <span>${S.notes.length}</span></h2><span class="mm-help">Kept on each project</span></div>
          <div class="mm-wlist arrive">${S.notes.map((x) => `<div class="mm-wrow"><span class="mm-no sm">${esc(x.projectNo || '–')}</span><span><b>${esc(x.text || '')}</b><span class="mm-meta">${esc(firstName(x.by || ''))} · on the project’s thread</span></span></div>`).join('') || '<div class="mm-empty">No notes.</div>'}</div></section>
      </div>
      <footer class="mm-foot">
        ${S.readOnly ? '' : `<button type="button" class="mm-btn primary${armed ? ' armed' : ''}" data-a="send"${mailOn ? '' : ' disabled aria-describedby="mm-mailnote"'}>${armed ? `Press again to email ${esc(word(n).toLowerCase())} ${n === 1 ? 'person' : 'people'}` : 'Send notes to the team'} ${kbd('Enter')}</button>
        <button type="button" class="mm-btn" data-a="finish">Finish without sending</button>`}
        <span class="mm-grow"></span>
        <span class="mm-keys" id="mm-mailnote">${mailOn ? `Emailed from ${esc(from)} to the ${esc(word(n).toLowerCase())} of you. Nothing goes to a client.`
    : 'Sending needs the company’s automation mailbox. Nothing goes to a client.'}</span>
      </footer>`;
  }
  async function finish(send) {
    if (S.saving) return;
    S.saving = true;
    const at = new Date().toISOString();
    if (S.meeting && S.meeting.id) {
      try {
        await updateMeeting(S.meeting.id, { state: 'wrapped', current: WRAP, endedAt: at, updatedAt: at,
          actionTaskIds: S.actionIds.slice(), changes: S.changes.slice(), noteIds: S.notes.map((n) => ({ id: n.id, projectNo: n.projectNo })),
          ...(S.roomFollows ? { roomFollows: false, room: null } : {}) });
        if (send) await updateMeeting(S.meeting.id, { sendNotes: true, updatedAt: at });
      } catch (e) { S.saving = false; toast(`The meeting could not be finished — ${e && e.message ? e.message : e}`); return; }
    }
    close();
    flash(send ? `Notes on their way to ${plural(S.order.length, 'person', 'people')}.` : 'Meeting finished.');
  }
  function sendNotes() {
    const btn = root.querySelector('[data-a="send"]');
    if (!btn || btn.disabled) return;
    if (S.armed && S.armed.kind === 'send') { finish(true); return; }
    S.armed = { kind: 'send' };
    drawBody();
    setTimeout(() => { if (S.armed && S.armed.kind === 'send' && root.isConnected) { S.armed = null; drawBody(); } }, 6000);
  }

  // ── The running list ("This meeting · 2 actions") ─────────────────────
  function openThisMeeting(btn) {
    const old = root.querySelector('.mm-pop'); if (old) { old.remove(); return; }
    const acts = S.actionIds.map((id) => S.made[id] || tasks.find((t) => t.id === id)).filter(Boolean);
    const pop = document.createElement('div');
    pop.className = 'mm-pop'; pop.setAttribute('role', 'dialog'); pop.setAttribute('aria-label', 'This meeting so far');
    pop.innerHTML = `<div class="mm-lab">Actions</div>${acts.map((t) => `<div class="mm-pop-row">${esc(t.title)}<span class="mm-meta">${esc(t.assigneeId ? firstName(userName(t.assigneeId)) : 'Nobody yet')}</span></div>`).join('') || '<div class="mm-meta">None yet</div>'}
      <div class="mm-lab">Changes</div>${S.changes.map((c) => `<div class="mm-pop-row">${esc(c.projectNo)} · ${esc(c.kind === 'stage' ? `${stageText(c.from)} → ${stageText(c.to)}` : c.kind === 'due' ? `Due ${c.to ? dm(c.to) : 'none'}` : `Lead: ${c.to}`)}</div>`).join('') || '<div class="mm-meta">None yet</div>'}
      <div class="mm-lab">Notes</div>${S.notes.map((n) => `<div class="mm-pop-row">${esc(n.projectNo)} · ${esc(String(n.text || '').slice(0, 80))}</div>`).join('') || '<div class="mm-meta">None yet</div>'}`;
    root.appendChild(pop);
    const r = btn.getBoundingClientRect();
    pop.style.right = `${Math.max(12, window.innerWidth - r.right)}px`;
    pop.style.bottom = `${Math.max(12, window.innerHeight - r.top + 8)}px`;
    const away2 = (ev) => { if (!pop.contains(ev.target) && ev.target !== btn) { pop.remove(); document.removeEventListener('pointerdown', away2, true); } };
    setTimeout(() => document.addEventListener('pointerdown', away2, true), 0);
  }

  // ── Wiring the body ────────────────────────────────────────────────────
  function wireBody(body, c) {
    body.querySelector('[data-a="prev"]')?.addEventListener('click', () => go(S.idx - 1));
    body.querySelector('[data-a="next"]')?.addEventListener('click', () => go(S.idx + 1));
    body.querySelector('[data-a="this"]')?.addEventListener('click', (ev) => openThisMeeting(ev.currentTarget));
    body.querySelector('[data-a="nodate"]')?.addEventListener('click', () => { S.noDate = true; drawBody(); });
    if (S.readOnly) return;
    if (c === WRAP) {
      body.querySelector('[data-a="send"]')?.addEventListener('click', () => sendNotes());
      body.querySelector('[data-a="finish"]')?.addEventListener('click', () => finish(false));
      return;
    }
    if (c === NOOWNER) {
      body.querySelectorAll('[data-give]').forEach((b) => b.addEventListener('click', () => give(b.dataset.key, b.dataset.give)));
      body.querySelectorAll('[data-undo]').forEach((b) => b.addEventListener('click', () => undoGive(b.dataset.undo)));
      body.querySelectorAll('.mm-nrow').forEach((r) => r.addEventListener('click', (ev) => {
        if (ev.target.closest('button')) return;
        const i = noOwnerItems().findIndex((x) => x.key === r.dataset.key);
        if (i >= 0 && i !== S.nsel) { S.nsel = i; drawBody(); }
      }));
      return;
    }
    // A person.
    body.querySelectorAll('.mm-prow').forEach((r) => r.querySelector('.mm-prow-main').addEventListener('click', () => {
      const list = pinSel(c);
      const i = list.findIndex((p) => p.id === r.dataset.pid);
      if (i >= 0 && i !== S.sel) { selectRow(i); S.armed = null; S.editing = null; drawBody(); }
    }));
    const p = selectedProject();
    const act = (k) => body.querySelector(`[data-row-act="${k}"]`);
    act('stage')?.addEventListener('click', (ev) => {
      if (!canEdit || !p) return;
      if (S.armed && S.armed.kind === 'stage' && S.armed.pid === p.id) { confirmStage(p, S.armed.to); return; }
      openStagePicker(ev.currentTarget, { number: p.number, status: p.status, onPick: (k) => { S.armed = { kind: 'stage', pid: p.id, to: k }; S.editing = null; drawBody(); } });
    });
    act('date')?.addEventListener('click', () => { if (!canEdit || !p) return; S.editing = { kind: 'date', pid: p.id }; S.armed = null; drawBody(); body.querySelector('#mm-date')?.focus(); });
    act('note')?.addEventListener('click', () => { if (!p) return; S.editing = { kind: 'note', pid: p.id }; S.armed = null; drawBody(); root.querySelector('#mm-note')?.focus(); });
    act('action')?.addEventListener('click', () => focusBar(p));
    body.querySelector('[data-ed="stage-yes"]')?.addEventListener('click', () => p && S.armed && confirmStage(p, S.armed.to));
    body.querySelectorAll('[data-ed="cancel"]').forEach((b) => b.addEventListener('click', () => { S.armed = null; S.editing = null; drawBody(); }));
    body.querySelector('[data-ed="date-save"]')?.addEventListener('click', () => p && saveDate(p, body.querySelector('#mm-date').value));
    body.querySelector('#mm-date')?.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') { ev.preventDefault(); saveDate(p, ev.target.value); } });
    body.querySelector('[data-ed="note-save"]')?.addEventListener('click', () => p && saveNote(p, body.querySelector('#mm-note').value));
    body.querySelector('#mm-note')?.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') { ev.preventDefault(); saveNote(p, ev.target.value); } });
    const bar = body.querySelector('[data-bar]');
    if (bar) {
      const input = bar.querySelector('.mm-bar-in');
      input.addEventListener('input', () => paintBar(bar));
      input.addEventListener('scroll', () => { bar.querySelector('.mm-mirror').scrollLeft = input.scrollLeft; });
      input.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter') { ev.preventDefault(); saveAction(bar); }
        if (ev.key === 'Backspace' && !input.value && S.barProject) { S.barProject = null; paintBar(bar); }
      });
      input.addEventListener('focus', () => bar.classList.add('is-focus'));
      input.addEventListener('blur', () => bar.classList.remove('is-focus'));
    }
    // "2 emails" under a stage: counted once per project, lazily (mail module only).
    if (hasFeature('mail') && S.mode === 'walkthrough') {
      const todo = projectsLedBy(projects, c).filter((x) => x.number && emails[x.number] == null);
      if (todo.length) {
        Promise.all(todo.map((x) => projectEmails(x.number, 60).then((rows) => {
          emails[x.number] = (Array.isArray(rows) ? rows : []).filter((m) => {
            const d = m && m.date && typeof m.date.toDate === 'function' ? m.date.toDate() : new Date(m && m.date);
            return !isNaN(d) && d.toISOString() >= since;
          }).length;
        }).catch(() => { emails[x.number] = 0; }))).then(() => {
          if (cur() !== c || !root.isConnected) return;
          if (!S.editing && !S.armed) refreshMoved(c);
          if (S.roomFollows) persist({ room: roomFor() });   // the room shows "2 emails" too
        });
      }
    }
  }
  function refreshMoved(c) {
    const vm = personVm(c);
    for (const r of vm.rows) {
      const host = root.querySelector(`.mm-prow[data-pid="${CSS.escape(r.p.id)}"] .mm-st`);
      if (!host) continue;
      const old = host.querySelector('.mm-moved');
      if (r.moved.text && (!old || old.textContent !== r.moved.text)) {
        if (old) old.remove();
        host.insertAdjacentHTML('beforeend', `<span class="mm-moved ${r.moved.tone}">${esc(r.moved.text)}</span>`);
      }
    }
  }

  // ── The room snapshot ──────────────────────────────────────────────────
  function roomFor() {
    const c = cur();
    const ids = S.order;
    const pos = ids.indexOf(c);
    const strip = ids.map((id, i) => { const p = personOf(id); return { initials: p ? p.initials : '', name: p ? p.name : '', state: i === pos ? 'now' : (pos < 0 || i < pos) ? 'done' : 'next' }; });
    const nextId = seq()[S.idx + 1];
    const base = { strip, next: nextId === NOOWNER ? 'No owner' : nextId === WRAP ? 'Wrap up' : userName(nextId), clocks: offs };
    if (c === NOOWNER) return roomSnapshot({ ...base, screen: 'noowner', sub: `${noOwnerItems().filter((x) => !S.given[x.key]).length} things nobody has yet` });
    if (c === WRAP) return roomSnapshot({ ...base, screen: 'wrap', sub: 'That’s everyone' });
    const vm = personVm(c);
    return roomSnapshot({
      ...base, screen: 'person', person: vm.person,
      sub: `${vm.person.role ? `${vm.person.role} · ` : ''}${plural(vm.rows.length, 'live project')}`,
      projects: S.mode === 'walkthrough' ? vm.rows.map((r) => ({ number: r.p.number, clientName: clientOf(r.p), title: r.p.title || '', status: r.p.status, moved: r.moved.text, due: r.due, late: r.late })) : [],
      openTasks: vm.open.length, overdue: vm.overdue, week: vm.week,
    });
  }

  // ══ FOLLOWING (remote office) ═════════════════════════════════════════
  let followSig = '';
  function startFollowing(m) {
    S.phase = 'follow'; S.readOnly = true; S.meeting = m;
    const apply = (rec) => {
      if (!rec) return;
      S.meeting = rec;
      S.mode = rec.mode || 'walkthrough';
      S.order = (rec.order || []).filter((id) => personOf(id));
      S.minutes = rec.minutesEach || 3;
      S.roomFollows = !!rec.roomFollows;
      S.followers = rec.followers || {};
      S.actionIds = Array.isArray(rec.actionTaskIds) ? rec.actionTaskIds.slice() : [];
      // What the runner changed, on this office's own copy of the projects —
      // a follower used to redraw only when the meeting moved to the next
      // person, and drew the stage it opened with.
      const recMoves = applyRecordedChanges(projectsById, rec.changes, applied, leadRefPath);
      recMoves.forEach(noteMove);
      const was = followSig; followSig = JSON.stringify([rec.changes || [], rec.actionTaskIds || [], rec.noteIds || [], rec.room || null]);
      const i = seq().indexOf(rec.current);
      const moved = i >= 0 && i !== S.idx;
      if (i >= 0) { S.idx = i; if (moved) { S.sel = 0; S.selPid = null; } }
      if (rec.state !== 'running') { close(); flash('The meeting has finished.'); return; }
      const top = root.querySelector('.mm-top');
      if (!top || !root.querySelector('.mm-body')) return;
      top.innerHTML = topInner(); wireTop();
      if (moved) drawBody();
      else if (recMoves.length || was !== followSig) drawBody({ quiet: true });
      else paintRing();
    };
    S.idx = Math.max(0, seq().indexOf(m.current));
    apply(m);
    beginRun();
    if (session && session.uid) followMeeting(m.id, session.uid, { name: session.displayName || (me && me.name) || session.email, initials: (me && me.initials) || initialsOf(session.displayName || ''), at: new Date().toISOString() }).catch(() => {});
    stopWatch = watchMeeting(m.id, apply);
  }
}

function flash(msg) {
  const t = document.createElement('div');
  t.className = 'mm-toast out'; t.setAttribute('role', 'status'); t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.remove(), 3200);
}
